import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Section } from 'interfaces/SectionTypes';
import { Alert, Button, Typography } from '@mui/material';

import SoftwareDownloadButton from 'components/service-center/SoftwareDownloadButton';

import { MyToverAPI } from '../../../api/MyToverAPI';
import PageSectionContainer from '../PageSectionContainer';

const SoftwareDownloadSection = ({ options }: Section) => {
  const { t } = useTranslation();
  const [binaryLoading, setBinaryLoading] = useState(false);
  const [binary, setBinary] = useState({
    url: '',
    version: '',
    hwversion: '',
    error: '',
  });
  const [error, setError] = useState<boolean | string>(false);
  // The request itself failed (network error, upstream down, ...) rather than the upstream
  // cleanly saying "no update for this hardware" - a transient hiccup, so this stays visible
  // (greyed out) instead of vanishing like the `error` case below.
  const [requestFailed, setRequestFailed] = useState(false);
  const { tovertafel: version } = options;

  async function getBinaryUpdateInfo() {
    try {
      setBinaryLoading(true);
      const { success, parsed } = await MyToverAPI.getSoftwareUpdate(version.toUpperCase());

      if (!success) {
        setRequestFailed(true);
        return;
      }

      if (parsed.data.url) {
        setBinary(parsed.data);
      } else {
        setError('service-center.software-update.not-available');
      }
    } catch (error) {
      setRequestFailed(true);
    } finally {
      setBinaryLoading(false);
    }
  }

  useEffect(() => {
    if (!binaryLoading && !binary.url) {
      getBinaryUpdateInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (binary.url) {
    return (
      <PageSectionContainer className="section__software-download">
        <SoftwareDownloadButton binary={binary} />
      </PageSectionContainer>
    );
  } else if (requestFailed) {
    return (
      <PageSectionContainer className="section__software-download">
        <Button id="software-download-unavailable" color="primary" fullWidth variant="contained" disabled>
          {t('service-center.software-update.download-unavailable')}
        </Button>
        <Typography
          id="software-download-temporarily-unavailable-hint"
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 1 }}
        >
          {t('service-center.software-update.temporarily-unavailable')}
        </Typography>
      </PageSectionContainer>
    );
  } else if (error) {
    return (
      <PageSectionContainer sx={{ p: 0 }}>
        <Alert id="software-download-not-available" severity="warning">
          {t(error)}
        </Alert>
      </PageSectionContainer>
    );
  }

  return null;
};
export default SoftwareDownloadSection;
